import { Directive, Input, OnInit, TemplateRef, ViewContainerRef } from '@angular/core'
import { CourtSystem } from '@ftr/contracts/api/court-system'
import { FeatureName } from '@ftr/contracts/type/core'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, mapData } from '@ftr/foundation'
import { EnabledCourtSystemFeaturesService } from '@ftr/ui-court-system'
import { Store } from '@ngxs/store'
import { map, switchMap } from 'rxjs'
import { UserState } from '../store'

/**
 * This directive shows or hides an element based on whether the court system
 * has a given feature enabled in the current court system.
 *
 * You can pass in a single FeatureName or you can pass in multiple. If you pass in multiple
 * features it will show if any of the passed in features are enabled (acting as an OR)
 *
 * If you wish to show an element if multiple features are enabled you can nest <ng-container>.
 *
 * NOTE: this only returns features that are represented in /admin/court-systems/:id/features.
 * Thus won't return if  audio playback is enabled.
 *
 * Also UserState logic is coupled to user permissions - so couldn't be used for say
 * the home page - as despite features being enabled - it would still return false
 * for users without user permissions.
 *
 * Example usage:
 * 1. Show div if the user's current court system has TranscriptOrdering feature enabled:
 * <div *ifCourtSystemFeatureEnabled="courtSystemFeatures.TranscriptOrdering"></div>
 *
 * 2. Show div if the user's current court system has either TranscriptOrdering or AudioOrdering
 * feature enabled:
 * <div
 *  *ifCourtSystemFeatureEnabled="[
 *    courtSystemFeatures.TranscriptOrdering,
 *    courtSystemFeatures.AudioOrdering,
 *  ]"
 * ></div>
 *
 * 3. Show div if the current court system has both TranscriptOrdering and AudioOrdering
 * feature enabled:
 * <ng-container *ifCourtSystemFeatureEnabled="courtSystemFeatures.TranscriptOrdering">
 *   <div *ifCourtSystemFeatureEnabled="courtSystemFeatures.AudioOrdering"></div>
 * </ng-container>
 *
 *
 * If you need a court system that is not "Current Court System". You can specify the court system id directly.
 *
 * *ifCourtSystemFeatureEnabled="courtSystemFeatures.TranscriptOrdering; courtSystemId: model.courtSystem.id"
 */
@Directive({
  selector: '[ifCourtSystemFeatureEnabled]',
  standalone: true,
})
export class IfCourtSystemFeatureEnabledDirective implements OnInit {
  private courtSystemFeatureName: FeatureName[]
  private courtSystemId: Uuid | undefined

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private store: Store,
    private enabledCourtSystemFeaturesService: EnabledCourtSystemFeaturesService,
  ) {}

  ngOnInit(): void {
    this.store
      .selectOnce(UserState.currentCourtSystem)
      .pipe(
        switchMap(currentCourtSystem => this.checkIfFeatureEnabled(currentCourtSystem)),
        map(x => x.isSuccess() && x.data),
      )
      .subscribe(hasCourtSystemFeatureEnabled => {
        if (hasCourtSystemFeatureEnabled) {
          this.viewContainer.createEmbeddedView(this.templateRef)
        } else {
          this.viewContainer.clear()
        }
      })
  }

  /**
   * If the court system we want, is the current court system, or if no court system is provided, use the store.
   *
   * Otherwise, make an api call to beast.
   */
  private checkIfFeatureEnabled(currentCourtSystem: CourtSystem | undefined): ApiResult<boolean> {
    if ((currentCourtSystem && this.courtSystemId === currentCourtSystem.id) || this.courtSystemId === undefined) {
      return this.store
        .select(UserState.currentCourtSystemEnabledFeatures)
        .pipe(
          mapData(d => d?.filter(f => f.isEnabled).some(f => this.courtSystemFeatureName.includes(f.name)) || false),
        )
    } else {
      return this.enabledCourtSystemFeaturesService.isAnyEnabled(this.courtSystemId, ...this.courtSystemFeatureName)
    }
  }

  @Input()
  set ifCourtSystemFeatureEnabledCourtSystemId(value: Uuid | undefined) {
    this.courtSystemId = value
  }

  @Input()
  set ifCourtSystemFeatureEnabled(courtSystemFeatureName: FeatureName | FeatureName[]) {
    this.courtSystemFeatureName = !Array.isArray(courtSystemFeatureName)
      ? [courtSystemFeatureName]
      : courtSystemFeatureName
  }
}
